import axios, {
  HttpStatusCode,
  type AxiosInstance,
  type AxiosRequestConfig,
} from 'axios';
import { ApiError } from './ApiError';
import type { IStringifyOptions } from 'qs';
import qs from 'qs';
import { requestSetHeadersInterceptor } from './requestSetHeadersInterceptor';
import { logError, logRequest, logResponse } from './log';
import { TIME } from 'shared/src/constant';
import { getAPIHost } from './getAPIHost/getAPIHost';

export interface IHttpClient {
  request<T>(
    config: CustomAxiosRequestConfig,
    paramSerializeOptions?: IStringifyOptions,
  ): Promise<{
    statusCode: HttpStatusCode;
    data: T;
  }>;
}

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  method: 'GET' | 'POST' | 'DELETE' | 'PATCH' | 'PUT';
  url: string;
}

class AxiosHttpClient {
  private readonly _axios: AxiosInstance;
  constructor(config: AxiosRequestConfig = {}) {
    this._axios = axios.create({
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: TIME.A_MINUTE,
      transitional: {
        clarifyTimeoutError: true,
      },
      ...config,
    });

    this._axios.interceptors.request.use(async request => {
      request.baseURL = await getAPIHost();
      await requestSetHeadersInterceptor(request);
      logRequest(request);
      return request;
    });

    this._axios.interceptors.response.use(
      response => {
        logResponse(response);
        return response;
      },
      error => {
        logError(error);
        throw new ApiError({
          message:
            error.response?.data?.message ??
            error.response?.data?.error_message ??
            error.response?.data?.response?.message ??
            error.response?.data?.detail ??
            error.message,
          code: error.response?.data?.code ?? null,
          type: error.response?.data?.type ?? null,
        });
      },
    );
  }
  async request<T>(
    config: Parameters<IHttpClient['request']>[0],
    paramSerializeOptions?: Parameters<IHttpClient['request']>[1],
  ): Promise<{
    statusCode: HttpStatusCode;
    data: T;
  }> {
    const response = await this._axios.request({
      ...config,

      paramsSerializer: params => {
        return qs.stringify(params, {
          ...paramSerializeOptions,
          encode: true,
        });
      },
    });

    return {
      statusCode: response.status,
      data: response.data,
    };
  }
}

export const defaultHttpClient = new AxiosHttpClient();
